/**
 * Keypoint block (🥖 / 🍱 … or 🟢/🟡/🟠 …) — the per-category "ключевые точки" line(s).
 *
 * 2026-09 (Alexey, Round-2): `clarify_category` in
 * services/.../daily_report/sections/empty_shelf.py was rewritten from a
 * 3-4-row-per-category format down to ONE line per category (bold→underline,
 * plus a "never reached fill threshold" fallback branch). Report bodies are
 * generated once and stored — older reports keep the OLD text forever, they
 * are never regenerated. So THIS PARSER MUST KEEP BOTH FORMATS WORKING:
 *   - OLD format: 3-4 rows, fixed category emoji (🥖/🍱/🍰/🧁), <b> highlights.
 *   - NEW format: exactly 1 row, a colour emoji (🟢/🟡/🟠) that encodes
 *     severity (not category), <u> highlights.
 * A block's `format` field records which shape it was parsed from; the form
 * (KeypointBlockForm.tsx) and the interactive chart (KeypointChartBlock.tsx)
 * both branch on it. Old-format parsing/serializing is UNCHANGED from
 * before this rewrite — only new-format support and the dispatch layer are
 * new.
 */

import { timeToMin, afterOpenTxt, afterTxtToMins } from './typedLines';
import { deriveClosingSeverity } from './treeBlock';

// ── category grammar (both formats) ──────────────────────────────────────
const CATEGORY_BY_EMOJI: Record<string, { name: string; gen: string }> = {
  '🥖': { name: 'Выпечка', gen: 'выпечки' },
  '🍱': { name: 'Готовая еда', gen: 'готовой еды' },
};
const CATEGORY_BY_NAME: Record<string, string> = {
  Выпечка: 'выпечки',
  'Готовая еда': 'готовой еды',
};

const OLD_KP_EMOJI = '🥖|🍱|🍰|🧁';
const NEW_COLOR_EMOJI = '🟢|🟡|🟠';
const EMOJI_LEAD_RE = new RegExp(`^(${OLD_KP_EMOJI}|${NEW_COLOR_EMOJI})`, 'u');

export interface KeypointBlockOld {
  format: 'old';
  name: string; // nominative — used by the fast-open sentence
  gen: string; // genitive — used everywhere else
  external: boolean; // whole block = "внешние причины"
  openTime: string | null; // HH:MM МСК working-hours open, for the "после открытия" calc
  emoji: string; // category emoji (🥖/🍱/🍰/🧁)
  openingFast: boolean; // "сразу при открытии" (no separate normalized row)
  openingPct: number;
  normalizedTime: string | null; // HH:MM МСК (normal opening only)
  declinePresent: boolean;
  declineTime: string | null; // HH:MM МСК
  closingPct: number;
}

export interface KeypointBlockNew {
  format: 'new';
  name: string; // nominative — appears directly in "{name}:" prefix
  gen: string; // genitive — used only by the external-reason sentence
  external: boolean;
  openTime: string | null; // pass-through, purely for the interactive chart's "open" pin position
  // 🟢/🟡/🟠 as PARSED. On write the circle is always re-derived from the
  // parameters (deriveKeypointColor — Alexey 2026-09-08: «цвета чётко
  // считались из того, что они вводят»); this field only records what the
  // stored row said.
  colorEmoji: string;
  neverFilled: boolean; // "never reached fill threshold" fallback variant (always 🟠)
  openingProblem: boolean; // opening was NOT "выкладка полная"
  openingPct: number; // meaningful only when openingProblem (or always, for neverFilled) — text omits it otherwise
  filledAt: string | null; // HH:MM — "доведена до нормы к" (openingProblem only)
  // As PARSED: whether the closing clause carried "последнее пополнение около".
  // On write it is re-derived from closingPct + category (deriveKeypointClosingSeverity).
  closingProblem: boolean;
  closingPct: number;
  replenishedAt: string | null; // HH:MM — "последнее пополнение около" (emitted only when the closing side is flagged)
  // neverFilled fallback only, as PARSED: whether the closing value was
  // highlighted. On write it is re-derived from closingPct + category — the
  // same closing_severity_of rule the backend applied. Words-vs-number wording
  // (closingPct <= 15) is derived too.
  closingHighlighted: boolean;
}

/** Mirrors the pre-tree backend `clarify_category` (bfa16896~1) closing
 *  severity for the one-line keypoint summary — the same
 *  `closing_severity_of` ladder the tree blocks use (bakery: ≥80 → 2,
 *  ≥30 → 1; other categories: ≤3 → 2, <20 → 1), keyed on the category NAME
 *  ("Выпечка" is the bakery). `next_day_closed` is schedule context the
 *  constructor doesn't have — same known gap as the tree form. */
export function deriveKeypointClosingSeverity(b: Pick<KeypointBlockNew, 'name' | 'closingPct'>): 0 | 1 | 2 {
  return deriveClosingSeverity(b.closingPct, b.name);
}

/** Mirrors the pre-tree backend colour ladder exactly:
 *    never_filled                      → 🟠 (hard-coded in the fallback branch)
 *    no opening AND no closing problem → 🟢
 *    both                              → 🟠  ("both sides wrong at once — escalate")
 *    one side                          → 🟠 if closing_severity ≥ 2 else 🟡
 *  (an opening-only problem is therefore always 🟡 — closing_severity is 0). */
export function deriveKeypointColor(b: KeypointBlockNew): string {
  if (b.neverFilled) return '🟠';
  const closingSeverity = deriveKeypointClosingSeverity(b);
  const closingProblem = closingSeverity > 0;
  if (!b.openingProblem && !closingProblem) return '🟢';
  if (b.openingProblem && closingProblem) return '🟠';
  return closingSeverity >= 2 ? '🟠' : '🟡';
}

export type KeypointBlock = KeypointBlockOld | KeypointBlockNew;

// ── OLD format regexes (unchanged) ────────────────────────────────────────
const RE_FAST = new RegExp(
  `^(${OLD_KP_EMOJI})\\s+(.+?)\\s+была выложена\\s+<b>сразу при открытии</b>\\.\\s+Заполненность на открытии:\\s*<b>\\s*(\\d+)%\\s*</b>\\s*$`,
  'u',
);
const RE_OPEN = new RegExp(
  `^(${OLD_KP_EMOJI})\\s+Заполненность\\s+(.+?)\\s+на открытии:\\s*<b>\\s*(\\d+)%\\s*</b>\\s*$`,
  'u',
);
const RE_NORM = new RegExp(
  `^(${OLD_KP_EMOJI})\\s+Заполненность\\s+(.+?)\\s+была доведена до нормы к\\s+<b>\\s*(\\d{1,2}:\\d{2})\\s*МСК\\s*</b>\\s*\\((.+?)\\)\\s*$`,
  'u',
);
const RE_DECLINE = new RegExp(
  `^(${OLD_KP_EMOJI})\\s+После\\s+<b>\\s*(\\d{1,2}:\\d{2})\\s*МСК\\s*</b>\\s+количество\\s+(.+?)\\s+начало снижаться и больше не пополнялось\\s*$`,
  'u',
);
const RE_CLOSE_PCT = new RegExp(
  `^(${OLD_KP_EMOJI})\\s+На момент закрытия заполненность зоны\\s+(.+?)\\s+<b>\\s*(\\d+)%\\s*</b>\\s*$`,
  'u',
);
const RE_CLOSE_EMPTY = new RegExp(
  `^(${OLD_KP_EMOJI})\\s+К моменту закрытия\\s+(.+?)\\s+<b>почти не было на прилавке</b>\\s*$`,
  'u',
);

// Shared by both formats — unchanged, still matches the current backend output.
const RE_EXTERNAL =
  /^В течение периода заполненность\s+(.+?)\s+не вышла на минимальный уровень\.\s+Вероятно, это связано с внешними причинами\.\s*$/u;

// ── NEW format regexes ─────────────────────────────────────────────────────
// Main path. Groups: 1 colour, 2 name, 3 openingPct?, 4 filledAt?,
// 5 closingPct-underlined?, 6 replenishedAt?, 7 closingPct-plain?
//
// The join style (comma vs period+capitalised "К") mirrors `opening_problem
// and closing_problem` in Python exactly — but when closing_problem is True
// with no replenished_at, the closing clause text itself falls back to the
// SAME plain "к закрытию осталось N%." text as the no-problem case (see
// empty_shelf.py's `closing_clause` ternary), making that one narrow
// combination textually indistinguishable from "no closing problem" except
// for the period-vs-comma join. Alexey confirmed it's fine to just read that
// as closingProblem=false (lossy on this one edge case) rather than also
// parsing the joiner — so this regex accepts either join style uniformly.
const RE_NEW_MAIN = new RegExp(
  '^(🟢|🟡|🟠)\\s+(.+?):\\s*' +
    '(?:на открытии выкладка полная' +
    '|на открытии выкладка\\s*<u>\\s*(\\d+)%\\s*</u>,\\s*доведена до нормы к\\s*<u>\\s*(\\d{1,2}:\\d{2})\\s*</u>)' +
    '(?:,\\s*|\\.\\s*)' +
    '[Кк] закрытию осталось\\s*' +
    '(?:<u>\\s*(\\d+)%\\s*</u>,\\s*последнее пополнение около\\s*<u>\\s*(\\d{1,2}:\\d{2})\\s*</u>' +
    '|(\\d+)%)' +
    '\\.\\s*$',
  'u',
);

// "Never reached fill threshold" fallback. Groups: 1 emoji(🟠), 2 name,
// 3 openingPct, 4 closingPct-underlined?, 5 closingPct-plain?. When neither
// 4 nor 5 matches, the text used the words-only "почти не было на прилавке".
const RE_NEW_NEVER_FILLED = new RegExp(
  '^(🟠)\\s+(.+?):\\s*на открытии\\s*<u>\\s*(\\d+)%\\s*</u>,\\s*в течение дня не достигала нормы,\\s*к закрытию\\s*' +
    '(?:<u>почти не было на прилавке</u>|<u>\\s*(\\d+)%\\s*</u>|(\\d+)%)' +
    '\\.\\s*$',
  'u',
);

function isOldFormatRow(row: string): boolean {
  return (
    RE_FAST.test(row) ||
    RE_OPEN.test(row) ||
    RE_NORM.test(row) ||
    RE_DECLINE.test(row) ||
    RE_CLOSE_PCT.test(row) ||
    RE_CLOSE_EMPTY.test(row)
  );
}
function isNewFormatRow(row: string): boolean {
  return RE_NEW_MAIN.test(row) || RE_NEW_NEVER_FILLED.test(row);
}

export function keypointEmoji(row: string): string | null {
  const m = EMOJI_LEAD_RE.exec(row);
  if (!m) return null;
  return isKeypointRow(row) ? m[1] : null;
}

/** Which keypoint kind a single row of an OLD-format block corresponds to —
 *  items 15-19: the interactive view gives each report LINE its own small
 *  card, colour-linked to the matching chart pin. NEW-format rows (exactly
 *  one row per category, potentially describing both an open AND a close
 *  event in one sentence) and the external-reason row don't map to a single
 *  kind — `null` for those; KeypointChartBlock falls back to rendering the
 *  whole row as one undifferentiated card in that case. */
export type KeypointRowKind = 'open' | 'norm' | 'decline' | 'close';
export function classifyKeypointRow(row: string): KeypointRowKind | null {
  if (RE_FAST.test(row) || RE_OPEN.test(row)) return 'open';
  if (RE_NORM.test(row)) return 'norm';
  if (RE_DECLINE.test(row)) return 'decline';
  if (RE_CLOSE_PCT.test(row) || RE_CLOSE_EMPTY.test(row)) return 'close';
  return null;
}

export function isKeypointRow(row: string): boolean {
  return isOldFormatRow(row) || isNewFormatRow(row) || RE_EXTERNAL.test(row);
}

// ── OLD format parse/serialize (unchanged behaviour) ──────────────────────
function deriveOpen(timeHhmm: string, afterTxt: string): string | null {
  const t = timeToMin(timeHhmm);
  const a = afterTxtToMins(afterTxt);
  if (t == null || a == null) return null;
  const open = t - a;
  return `${String(Math.floor(open / 60)).padStart(2, '0')}:${String(open % 60).padStart(2, '0')}`;
}

function parseOldFormatBlock(rows: string[], openTime: string | null): KeypointBlockOld {
  const emojiM = EMOJI_LEAD_RE.exec(rows[0] ?? '');
  const emoji = emojiM ? emojiM[1] : '';
  const cat = CATEGORY_BY_EMOJI[emoji];
  const block: KeypointBlockOld = {
    format: 'old',
    emoji,
    name: cat?.name ?? '',
    gen: cat?.gen ?? '',
    external: false,
    openingFast: false,
    openingPct: 0,
    normalizedTime: null,
    declinePresent: false,
    declineTime: null,
    closingPct: 0,
    openTime,
  };

  for (const row of rows) {
    let m: RegExpExecArray | null;
    if ((m = RE_EXTERNAL.exec(row))) {
      block.external = true;
      block.gen = m[1];
    } else if ((m = RE_FAST.exec(row))) {
      block.openingFast = true;
      block.name = m[2];
      block.openingPct = Number(m[3]);
    } else if ((m = RE_OPEN.exec(row))) {
      block.gen = m[2];
      block.openingPct = Number(m[3]);
    } else if ((m = RE_NORM.exec(row))) {
      block.gen = m[2];
      block.normalizedTime = m[3];
      if (!block.openTime) block.openTime = deriveOpen(m[3], m[4]);
    } else if ((m = RE_DECLINE.exec(row))) {
      block.declinePresent = true;
      block.declineTime = m[2];
      block.gen = m[3];
    } else if ((m = RE_CLOSE_PCT.exec(row))) {
      block.gen = m[2];
      block.closingPct = Number(m[3]);
    } else if ((m = RE_CLOSE_EMPTY.exec(row))) {
      block.gen = m[2];
      block.closingPct = 10; // "почти не было" ⇒ ≤15%; seed a representative value
    }
  }

  // Backfill grammar from the known category when one form was absent.
  if (!block.name) block.name = cat?.name ?? block.gen;
  if (!block.gen) block.gen = cat?.gen ?? block.name;
  return block;
}

function serializeOldFormatBlock(b: KeypointBlockOld): string[] {
  if (b.external) {
    return [
      `В течение периода заполненность ${b.gen} не вышла на минимальный уровень. Вероятно, это связано с внешними причинами.`,
    ];
  }

  const rows: string[] = [];
  const pct = (n: number) => Math.max(0, Math.min(100, Math.round(n || 0)));

  if (b.openingFast) {
    rows.push(
      `${b.emoji} ${b.name} была выложена <b>сразу при открытии</b>. Заполненность на открытии: <b>${pct(b.openingPct)}%</b>`,
    );
  } else {
    rows.push(`${b.emoji} Заполненность ${b.gen} на открытии: <b>${pct(b.openingPct)}%</b>`);
    const t = b.normalizedTime ?? '';
    const tMin = timeToMin(t);
    const oMin = timeToMin(b.openTime ?? '');
    const after = tMin != null && oMin != null ? afterOpenTxt(Math.max(0, tMin - oMin)) : '';
    rows.push(
      `${b.emoji} Заполненность ${b.gen} была доведена до нормы к <b>${t} МСК</b> (${after})`,
    );
  }

  if (b.declinePresent && b.declineTime) {
    rows.push(
      `${b.emoji} После <b>${b.declineTime} МСК</b> количество ${b.gen} начало снижаться и больше не пополнялось`,
    );
  }

  rows.push(
    pct(b.closingPct) <= 15
      ? `${b.emoji} К моменту закрытия ${b.gen} <b>почти не было на прилавке</b>`
      : `${b.emoji} На момент закрытия заполненность зоны ${b.gen} <b>${pct(b.closingPct)}%</b>`,
  );

  return rows;
}

// ── NEW format parse/serialize ─────────────────────────────────────────────
function parseNewFormatBlock(rows: string[], openTime: string | null): KeypointBlockNew {
  const row = rows[0] ?? '';
  let m: RegExpExecArray | null;

  const base: KeypointBlockNew = {
    format: 'new',
    name: '',
    gen: '',
    external: false,
    openTime,
    colorEmoji: '🟢',
    neverFilled: false,
    openingProblem: false,
    openingPct: 100,
    filledAt: null,
    closingProblem: false,
    closingPct: 0,
    replenishedAt: null,
    closingHighlighted: false,
  };

  if ((m = RE_EXTERNAL.exec(row))) {
    const gen = m[1];
    // Reverse-lookup a nominative name from the known genitives; falls back
    // to the genitive text itself (matches the old parser's same fallback).
    const name = Object.entries(CATEGORY_BY_NAME).find(([, g]) => g === gen)?.[0] ?? gen;
    return { ...base, external: true, name, gen };
  }

  if ((m = RE_NEW_NEVER_FILLED.exec(row))) {
    const name = m[2].trim();
    const openingPct = Number(m[3]);
    // Highlighted unless the plain-pct branch matched (group 5); the
    // words-only "почти не было" branch (neither group set) is highlighted too.
    const closingHighlighted = m[5] === undefined;
    const closingPct = m[4] !== undefined ? Number(m[4]) : m[5] !== undefined ? Number(m[5]) : 10;
    return {
      ...base,
      colorEmoji: m[1],
      name,
      gen: CATEGORY_BY_NAME[name] ?? name,
      neverFilled: true,
      openingPct,
      closingPct,
      closingHighlighted,
    };
  }

  if ((m = RE_NEW_MAIN.exec(row))) {
    const name = m[2].trim();
    const openingProblem = m[3] !== undefined;
    const closingProblem = m[5] !== undefined;
    return {
      ...base,
      colorEmoji: m[1],
      name,
      gen: CATEGORY_BY_NAME[name] ?? name,
      openingProblem,
      openingPct: openingProblem ? Number(m[3]) : 100,
      filledAt: openingProblem ? m[4] : null,
      closingProblem,
      closingPct: closingProblem ? Number(m[5]) : Number(m[7]),
      replenishedAt: closingProblem ? m[6] : null,
    };
  }

  // Unreachable in practice — parseKeypointBlock only routes here after
  // isKeypointRow matched — but keep a safe default rather than throwing.
  return base;
}

function serializeNewFormatBlock(b: KeypointBlockNew): string[] {
  if (b.external) {
    return [
      `В течение периода заполненность ${b.gen} не вышла на минимальный уровень. Вероятно, это связано с внешними причинами.`,
    ];
  }

  const pct = (n: number) => Math.max(0, Math.min(100, Math.round(n || 0)));
  // Colour / closing flags are never taken from the block as parsed — always
  // re-derived from the numbers (see deriveKeypointColor).
  const closingProblem = deriveKeypointClosingSeverity(b) > 0;

  if (b.neverFilled) {
    const openingPct = pct(b.openingPct);
    const closingPct = pct(b.closingPct);
    let closingDesc: string;
    if (!closingProblem) {
      closingDesc = `${closingPct}%`;
    } else if (closingPct <= 15) {
      closingDesc = '<u>почти не было на прилавке</u>';
    } else {
      closingDesc = `<u>${closingPct}%</u>`;
    }
    return [
      `🟠 ${b.name}: на открытии <u>${openingPct}%</u>, в течение дня не достигала нормы, к закрытию ${closingDesc}.`,
    ];
  }

  const openingClause = b.openingProblem
    ? `на открытии выкладка <u>${pct(b.openingPct)}%</u>, доведена до нормы к <u>${b.filledAt ?? ''}</u>`
    : 'на открытии выкладка полная';

  // Mirrors the backend's `closing_problem and replenished_at is not None`
  // guard: a flagged closing side with no observed restock prints the plain
  // clause (the lossy edge RE_NEW_MAIN's comment describes).
  const closingClause =
    closingProblem && b.replenishedAt
      ? `к закрытию осталось <u>${pct(b.closingPct)}%</u>, последнее пополнение около <u>${b.replenishedAt}</u>.`
      : `к закрытию осталось ${pct(b.closingPct)}%.`;

  // Mirrors Python's `if opening_problem and closing_problem:` exactly — the
  // join style depends on the two booleans, independent of whether
  // closing_clause itself ended up highlighted (see RE_NEW_MAIN's comment).
  const summary =
    b.openingProblem && closingProblem
      ? `${openingClause}. ${closingClause[0].toUpperCase()}${closingClause.slice(1)}`
      : `${openingClause}, ${closingClause}`;

  return [`${deriveKeypointColor(b)} ${b.name}: ${summary}`];
}

// ── dispatch ─────────────────────────────────────────────────────────────
export function parseKeypointBlock(rows: string[], openTime: string | null): KeypointBlock {
  if (rows.some(isOldFormatRow)) {
    return parseOldFormatBlock(rows, openTime);
  }
  return parseNewFormatBlock(rows, openTime);
}

export function serializeKeypointBlock(b: KeypointBlock): string[] {
  return b.format === 'old' ? serializeOldFormatBlock(b) : serializeNewFormatBlock(b);
}

// ── cross-format accessors (used by KeypointChartBlock.tsx) ───────────────
export function getFilledAt(b: KeypointBlock): string | null {
  return b.format === 'old' ? b.normalizedTime : b.filledAt;
}
export function getReplenishedAt(b: KeypointBlock): string | null {
  return b.format === 'old' ? b.declineTime : b.replenishedAt;
}
export function hasFilledMarker(b: KeypointBlock): boolean {
  return b.format === 'old' ? !b.openingFast : !b.neverFilled && b.openingProblem;
}
export function hasReplenishedMarker(b: KeypointBlock): boolean {
  return b.format === 'old' ? b.declinePresent : !b.neverFilled && b.closingProblem;
}
